import { useState, useEffect, useMemo } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { EVENT_REGISTRANTS_QUERY, GENERATE_CANCELLATION_EXPORT_MUTATION } from "../../queries";
import {
  EventRegistrantNode,
  EventRegistrants,
  Variables,
  RegistrationStatus,
  OrderStatus,
  RegistrantRow
} from "../../types";
import { toDateFormat } from "@/lib/dateHelpers";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import Table, { TableControls, TableFooter, toTableControlActions } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import SearchInput from "@/components/SearchInput";
import { useNavigate } from "@/router";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import usePagination from "@/hooks/usePagination";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import { EventOutletContext } from "@/pages/events/[eventSlug]/(tabbed)/_layout";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { useEventsBasePath } from "@/pages/events/_eventsBasePathContext";
import { getColumns } from "./_components/TableColumns";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

export default function CancellationsPage() {
  const { eventSlug = "" } = useParams();
  const navigate = useNavigate();
  const basePath = useEventsBasePath();
  const { eventData } = useOutletContext<EventOutletContext>();
  const { isCompactLayout } = useIsCompactLayout();
  const { trackEvent } = useAmplitude();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });
  const [variables, setVariables] = useState<Variables>({
    eventSlug,
    status: RegistrationStatus.CANCELED
  });
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | null>(null);
  const { Menu, setMenu } = useTableRowMenu();

  useEffect(() => {
    resetPagination();
    setVariables((prevVariables) => ({
      ...(debouncedSearch
        ? { ...prevVariables, eventSlug, search: debouncedSearch }
        : { eventSlug, status: RegistrationStatus.CANCELED })
    }));
  }, [debouncedSearch, eventSlug, resetPagination]);

  const { data, loading: queryLoading } = useQuery<EventRegistrants>(EVENT_REGISTRANTS_QUERY, {
    variables: {
      status: RegistrationStatus.CANCELED,
      ...variables,
      ...paginationVariables
    }
  });

  const [generateCancellationExport, { loading: mutationLoading }] = useMutation(GENERATE_CANCELLATION_EXPORT_MUTATION);

  const handleCsvExport = async () => {
    try {
      const { data } = await generateCancellationExport({
        variables: {
          input: {
            eventSlug
          }
        }
      });
      const downloadUrl = data.generateCancellationExport.url;
      if (!downloadUrl) {
        throw new Error("Download URL not found.");
      }
      trackEvent(
        EVENT_ANALYTICS_EVENTS.cancellationsExported,
        getEventAnalyticsData({
          source: "exports",
          eventId: eventData?.id,
          eventSlug,
          action: "export",
          field: "cancellations"
        })
      );
      window.location.assign(downloadUrl);
    } catch (error) {
      alert("Unable to export cancellations");
      console.error(error);
    }
  };

  const registrationsData: RegistrantRow[] = (data?.eventRegistrants.edges || []).map(
    ({ node: { registeredAt, canceledAt, order, ...registrant } }: EventRegistrantNode) => ({
      ...registrant,
      registeredAt: toDateFormat(registeredAt) || "",
      canceledAt: canceledAt ? toDateFormat(canceledAt) || "" : "",
      orderStatus: order?.status as OrderStatus,
      orderTotalPaid: order?.totalPaid,
      amountRefunded: order ? centsToDollarsFormatted(order.totalRefunded || 0) || undefined : undefined,
      paymentStatus: order?.paymentStatus,
      additionalGuests: registrant.customFields?.additional_guests || 0
    })
  );

  const hasAdditionalGuests = !!eventData?.customFields?.includes("additional_guests");
  const hasOrderColumns = registrationsData.some((row) => row.orderStatus);

  const columns = useMemo(
    () =>
      getColumns({
        isCompactLayout,
        hasAdditionalGuests,
        hasOrderColumns,
        setMenu,
        onViewPerson: (pgaId) => {
          setSelectedPgaId(pgaId);
          setIsPersonSideSheetOpen(true);
        },
        onViewRegistration: (registrationId) => {
          navigate(`${basePath}/${eventSlug}/registrations/${registrationId}` as any);
        }
      }),
    [isCompactLayout, hasAdditionalGuests, hasOrderColumns, setMenu, navigate, basePath, eventSlug]
  );

  return (
    <>
      <Menu />
      <div className="pt-12" />
      <Table
        data={registrationsData}
        loading={queryLoading}
        columns={columns}
        getRowLink={(row) => ({
          to: `${basePath}/${eventSlug}/registrations/${row.original.id}` as any
        })}
        hasActiveFilters={!!debouncedSearch}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Cancellations" caption="This event does not have any cancellations" iconName="person" />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            actions={
              registrationsData.length > 0
                ? toTableControlActions([
                    {
                      label: "Export Current List",
                      variant: "text",
                      icon: "download",
                      disabled: mutationLoading,
                      onClick: handleCsvExport
                    }
                  ])
                : []
            }
            pagination={createHeaderPagination(data?.eventRegistrants, {
              pageCount: registrationsData.length,
              loading: queryLoading
            })}
            onSortClick={onSortClick}
          >
            <SearchInput
              placeholder="Search Registrants"
              query={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                resetPagination();
              }}
              onClear={() => {
                clearSearch();
                resetPagination();
              }}
            />
          </TableControls>
        )}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(data?.eventRegistrants, {
              pageCount: registrationsData.length,
              loading: queryLoading
            })}
          />
        )}
      />
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(null);
        }}
        pgaId={selectedPgaId}
      />
    </>
  );
}
